"use client";

import { useEffect, useRef } from "react";
import { ArrowRight, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CallToAction() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      draw();
    };
    const draw = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const hexSize = 36;
      const hexWidth = hexSize * 2;
      const hexHeight = Math.sqrt(3) * hexSize;
      const cols = Math.ceil(canvas.width / (hexWidth * 0.75)) + 2;
      const rows = Math.ceil(canvas.height / hexHeight) + 2;
      ctx.strokeStyle = "rgba(215, 25, 12, 0.05)";
      ctx.lineWidth = 1;
      for (let col = -1; col < cols; col++) {
        for (let row = -1; row < rows; row++) {
          const x = col * hexWidth * 0.75;
          const y = row * hexHeight + (col % 2 === 0 ? 0 : hexHeight / 2);
          drawHex(ctx, x, y, hexSize);
        }
      }
    };
    const drawHex = (ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number) => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = Math.PI / 180 * (60 * i - 30);
        const x = cx + size * Math.cos(angle);
        const y = cy + size * Math.sin(angle);
        if (i === 0) ctx.moveTo(x, y);else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.stroke();
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);
  return <section data-section-id="232273" id="samplereportcta" className="relative w-full overflow-hidden bg-background text-foreground">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{
      opacity: 0.3
    }} />

      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse 70% 60% at 50% 100%, hsla(25, 95%, 53%, 0.05) 0%, transparent 70%)"
    }} />
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse 50% 40% at 50% 0%, hsla(215, 25%, 12%, 0.03) 0%, transparent 60%)"
    }} />

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-32 flex flex-col items-center text-center">
        <div ref={sectionRef} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="outline" className="mb-8 px-5 py-2 rounded-lg text-xs font-bold tracking-widest uppercase text-primary gap-2" style={{
          borderColor: "hsl(25, 95%, 53%)",
          background: "hsla(25, 95%, 53%, 0.08)"
        }}>
            <span className="inline-block w-2 h-2 rounded-lg shrink-0" style={{
            background: "hsl(25, 95%, 53%)"
          }} />
            Schedule an Inspection
          </Badge>
        </div>

        <h2 className={`font-default font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6 text-secondary transition-all duration-700 ease-out delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          Ready to Schedule Your{" "}
          <span className="text-primary">Inspection?</span>
        </h2>

        <p className={`font-default text-base sm:text-lg max-w-2xl mb-10 leading-relaxed transition-all duration-700 ease-out delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
        color: "hsl(215, 15%, 40%)"
      }}>
          Get clear, dependable inspection reports to protect your property investment. Book your South Florida inspection online today or call us directly.
        </p>

        <div className={`flex flex-col sm:flex-row items-center gap-4 transition-all duration-700 ease-out delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Button asChild size="lg" className="font-default font-bold text-base px-8 py-6 rounded-lg border-0 transition-all duration-300 hover:scale-105" style={{
          background: "hsl(25, 95%, 53%)",
          color: "hsl(0, 0%, 100%)",
          boxShadow: "0 10px 20px -5px hsla(25, 95%, 53%, 0.3)"
        }}>
            <Link to="#schedule-your-inspection" className="flex items-center gap-2">
              Schedule Your Inspection
              <ArrowRight className="size-5 shrink-0" />
            </Link>
          </Button>

          <Button asChild variant="ghost" size="lg" className="font-default font-bold text-base px-8 py-6 rounded-lg border transition-all duration-300 hover:scale-105" style={{
          borderColor: "hsl(215, 25%, 12%)",
          color: "hsl(215, 25%, 12%)",
          background: "transparent"
        }}>
            <a href="tel:+17863826895" className="flex items-center gap-2">
              <Phone className="size-5 shrink-0" />
              Call or Text Us
            </a>
          </Button>
        </div>
      </div>
    </section>;
}